import get from "lodash/get";
import isEqual from "lodash/isEqual";
import React, {
  ChangeEvent,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import Alert from "veneer/Alert";
import Button from "veneer/Button";
import Stack from "veneer/Stack";
import Typography from "veneer/Typography";

import { APP_MANIFEST_V3 } from "cortex/ui/src/constant/application";
import { STABLE, TESTING } from "cortex/ui/src/fleet/constant/appVersionType";
import {
  Application,
  ApplicationRestriction,
  ApplicationVersion,
  Schema,
  UserParam,
} from "cortex/ui/src/fleet/view/application/model";
import { captureStack } from "cortex/ui/src/integrations/sentry";
import { Network } from "cortex/ui/src/network/model";
import BackButton from "cortex/ui/src/ui/button";
import AppCheckboxGroup from "cortex/ui/src/ui/checkbox";
import { FetchController } from "cortex/ui/src/ui/fetch";
import { List } from "cortex/ui/src/ui/list";
import Notification from "cortex/ui/src/ui/notification";
import { AppParameterFormLayout } from "cortex/ui/src/ui/parameter-form/AppParamFormLayout";
import { CustomParameterForm } from "cortex/ui/src/ui/parameter-form/CustomParameterForm";
import { PRIORITY, Priority } from "cortex/ui/src/ui/parameter-form/utils";
import { devLogger } from "cortex/ui/src/util/logging";
import { difference } from "cortex/ui/src/util/set";
import { sortByProp } from "cortex/ui/src/util/sortByProp";
import { Panel, StickyPageActions } from "cortex/ui/src/view/layout";

import { Severity, captureMessage } from "@sentry/browser";

const sortByAppDisplayNameAsc = sortByProp.bind(
  null,
  "app.display_name",
  "asc",
);

interface ConfigureParamsProps {
  addErrorMessage: (msg: string) => void;
  apps: Application[];
  deviceParams?: UserParam[];
  network: Network;
  onSubmit: (params: SubmittedParam[]) => Promise<void>;
  priority: Priority;
  readonly: boolean;
  showTestingApps?: boolean;
  successLink: ReactNode;
  systemManagedParams?: string[] | null;
}

interface AppFormApi {
  form: {
    submitForm: () => Promise<void>;
  };
  getFormValues: () => Record<string, unknown>;
}

interface AppSchemaEntry {
  app: Application;
  schemas: Schema[];
  version: ApplicationVersion;
}

interface CustomParam {
  k: string;
  v: unknown;
}

interface FormErrorShape {
  [key: string]: unknown;
}
interface RestrictedAppVersions {
  [appId: number]: ApplicationVersion | null;
}

interface SubmittedParam {
  appId?: number;
  name: string;
  value: unknown;
}

interface CustomParameterFormHandle {
  getParams: () => CustomParam[];
}

function filterEmptyValues([, value]: [string, unknown]) {
  if (value === undefined) {
    return false;
  }

  if (typeof value === "string" && !value) {
    return false;
  }

  return true;
}

function getFormValues(
  formValues: Record<number, () => Record<string, unknown>>,
): Record<number, Record<string, unknown>> {
  return Object.entries(formValues).reduce(
    (acc, [appId, getAppFormValues]) => {
      acc[Number(appId)] = getAppFormValues();
      return acc;
    },
    {} as Record<number, Record<string, unknown>>,
  );
}

function transformAppFormState(
  formValues: Record<number, Record<string, unknown>>,
): SubmittedParam[] {
  return Object.entries(formValues).reduce<SubmittedParam[]>(
    (acc, [appId, values]) => {
      const appParams = Object.entries(values)
        .filter(filterEmptyValues)
        .map(([name, value]) => ({
          appId: Number(appId),
          name,
          value,
        }));

      return acc.concat(appParams);
    },
    [],
  );
}

function transformCustomParamState(params: CustomParam[]): SubmittedParam[] {
  return params.map(({ k, v }) => ({
    name: k,
    value: v,
  }));
}

export default function ConfigureParams({
  addErrorMessage,
  apps,
  deviceParams = [],
  network,
  onSubmit,
  priority,
  readonly,
  showTestingApps = false,
  successLink,
  systemManagedParams,
}: ConfigureParamsProps) {
  const [fieldErrors, setFieldErrors] = useState<FormErrorShape>({});
  const [nonFieldErrors, setNonFieldErrors] = useState<string[]>([]);
  const [pristineApps, setPristineApps] = useState<Set<number>>(new Set());
  const [priorityParams, setPriorityParams] = useState<UserParam[]>([]);
  const [selectedApps, setSelectedApps] = useState<Set<number>>(new Set());
  const [submitted, setSubmitted] = useState(false);
  const [successMessage] = useState("");

  const appFormsRef = useRef<Record<number, () => AppFormApi>>({});
  const formRef = useRef<CustomParameterFormHandle | null>(null);
  const isMountedRef = useRef(true);

  const getNetworkParams = useCallback((): UserParam[] => {
    return [...(get(network, "user_params", []) as ReadonlyArray<UserParam>)];
  }, [network]);

  const getPriorityParams = useCallback((): UserParam[] => {
    if (priority === PRIORITY.DEVICE) {
      return deviceParams;
    }

    if (priority === PRIORITY.NETWORK) {
      return getNetworkParams();
    }

    throw new Error("Unhandled priority.");
  }, [deviceParams, getNetworkParams, priority]);

  const appNotFoundWarning = useCallback((appId: number, networkId: number) => {
    const warning =
      "Restricted app not found. " +
      `Network Id: ${networkId}, App Id: ${appId}`;

    devLogger(console.warn, warning);
    captureStack();
    captureMessage(warning, Severity.Warning);
  }, []);

  const appVersionNotFoundWarning = useCallback(
    (appId: number, appVersionId: number, networkId: number) => {
      const warning =
        `App version not found. Network Id: ${networkId}, ` +
        `App Id: ${appId}, Version Id: ${appVersionId}`;

      devLogger(console.warn, warning);
      captureStack();
      captureMessage(warning, Severity.Warning);
    },
    [],
  );

  const getRestrictedAppVersions = useCallback(
    (
      currentApps: ReadonlyArray<Application>,
      restrictions: ReadonlyArray<ApplicationRestriction>,
      showTesting: boolean,
    ): RestrictedAppVersions => {
      if (priority === PRIORITY.DEVICE && showTesting) {
        return {};
      }

      return restrictions.reduce<RestrictedAppVersions>((acc, restriction) => {
        const app = currentApps.find((item) => item.id === restriction.app_id);

        if (!app) {
          acc[restriction.app_id] = null;
          appNotFoundWarning(restriction.app_id, network.id);
          return acc;
        }

        const appVersion =
          app.versions.find(
            (version: ApplicationVersion) =>
              version.id === restriction.version_id,
          ) || null;

        acc[app.id] = appVersion;

        if (!appVersion) {
          appVersionNotFoundWarning(app.id, restriction.version_id, network.id);
        }

        return acc;
      }, {});
    },
    [appNotFoundWarning, appVersionNotFoundWarning, network.id, priority],
  );

  const getAppParamSchemas = useCallback(
    (
      currentApps: ReadonlyArray<Application>,
      options: { showTesting: boolean },
      restrictedAppVersions: RestrictedAppVersions,
    ): AppSchemaEntry[] => {
      const appType = options.showTesting ? TESTING : STABLE;

      return currentApps
        .map((app) => {
          const version = get(restrictedAppVersions, app.id, app[appType]);
          return [app, version] as const;
        })
        .filter(
          (entry): entry is readonly [Application, ApplicationVersion] => {
            const [, version] = entry;
            return Boolean(
              version && version.manifest_version === APP_MANIFEST_V3,
            );
          },
        )
        .map(([app, version]) => ({
          app,
          schemas: [...version.user_params] as Schema[],
          version,
        }))
        .sort(sortByAppDisplayNameAsc);
    },
    [],
  );

  const registerAppForm = useCallback(
    (appId: number, form: () => AppFormApi) => {
      appFormsRef.current[appId] = form;
    },
    [],
  );

  const removeAppParamForm = useCallback((appId: number) => {
    setSelectedApps((prev) => {
      const next = new Set(prev);
      next.delete(appId);
      return next;
    });

    delete appFormsRef.current[appId];
  }, []);

  const collectAppFormState = useCallback(
    (appForms: Record<number, () => AppFormApi>) => {
      const formValues: Record<number, () => Record<string, unknown>> = {};
      const promises: Promise<void>[] = [];

      Object.entries(appForms).forEach(([appId, getAppFormApi]) => {
        const { form, getFormValues: getAppFormValues } = getAppFormApi();
        formValues[Number(appId)] = getAppFormValues;
        promises.push(form.submitForm());
      });

      return Promise.all(promises)
        .then(() => getFormValues(formValues))
        .catch(() => {
          const errorMessage = "Please address form errors.";
          const error = new Error(errorMessage) as Error & {
            data?: {
              field_errors: Record<string, unknown>;
              non_field_errors: string[];
            };
          };

          error.data = {
            field_errors: {},
            non_field_errors: [errorMessage],
          };

          return Promise.reject(error);
        });
    },
    [],
  );

  const updateParams = useCallback(() => {
    const rawCustomParams = formRef.current?.getParams() ?? [];
    const customParams = transformCustomParamState(rawCustomParams);

    return collectAppFormState(appFormsRef.current).then((formValues) => {
      const appParams = transformAppFormState(formValues);
      const params = [...customParams, ...appParams];
      return onSubmit(params);
    });
  }, [collectAppFormState, onSubmit]);

  const handleSubmit = useCallback(() => {
    setFieldErrors({});
    setNonFieldErrors([]);
    setSubmitted(true);

    updateParams()
      .catch((e: unknown) => {
        if (!isMountedRef.current) {
          return;
        }

        setFieldErrors(get(e, "data.field_errors") || {});
        setNonFieldErrors(get(e, "data.non_field_errors") || []);

        const message = get(e, "message");
        if (message) {
          addErrorMessage(
            "Sorry, there was an error submitting your request: " + message,
          );
        }
      })
      .then(() => {
        if (!isMountedRef.current) {
          return;
        }

        setSubmitted(false);
      });
  }, [addErrorMessage, updateParams]);

  const onClickAppIcon = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const { checked, dataset } = e.target;
      const appId = Number(dataset.id);

      if (checked) {
        setSelectedApps((prev) => new Set(prev).add(appId));
        return;
      }

      removeAppParamForm(appId);
    },
    [removeAppParamForm],
  );

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const nextPriorityParams = getPriorityParams();

    if (!isEqual(priorityParams, nextPriorityParams)) {
      setPriorityParams(nextPriorityParams);

      const nextSelectedApps = new Set<number>([
        ...selectedApps,
        ...nextPriorityParams
          .filter((p) => p.app_id)
          .map((p) => Number(p.app_id)),
      ]);

      if (!isEqual(nextSelectedApps, selectedApps)) {
        setPristineApps(new Set(nextSelectedApps));
        setSelectedApps(nextSelectedApps);
      }
    }
  }, [getPriorityParams, priorityParams, selectedApps]);

  const hasAppRestrictions = Boolean(network.app_restrictions?.length);
  const networkParams = useMemo(() => getNetworkParams(), [getNetworkParams]);
  const appRestrictions = useMemo<ApplicationRestriction[]>(() => {
    return (network.app_restrictions || []).reduce<ApplicationRestriction[]>(
      (acc, restriction) => {
        if (typeof restriction.app_id !== "number") {
          return acc;
        }

        acc.push({
          app_id: restriction.app_id,
          version_id: Number(restriction.version_id),
        });

        return acc;
      },
      [],
    );
  }, [network.app_restrictions]);
  const devCustomParams = useMemo(
    () => deviceParams.filter((p) => !p.app_id),
    [deviceParams],
  );
  const netCustomParams = useMemo(
    () => networkParams.filter((p) => !p.app_id),
    [networkParams],
  );
  const appsChanged = useMemo(
    () => difference(pristineApps, selectedApps).size > 0,
    [pristineApps, selectedApps],
  );
  const restrictedAppVersions = useMemo(
    () => getRestrictedAppVersions(apps, appRestrictions, showTestingApps),
    [appRestrictions, apps, getRestrictedAppVersions, showTestingApps],
  );
  const applications = useMemo(
    () =>
      getAppParamSchemas(
        apps,
        { showTesting: showTestingApps },
        restrictedAppVersions,
      ),
    [apps, getAppParamSchemas, restrictedAppVersions, showTestingApps],
  );

  const errList = nonFieldErrors.length ? (
    <List items={nonFieldErrors} />
  ) : null;

  const renderApps = () => {
    if (applications.length === 0) {
      return <p className="no-v3-apps">No applications available.</p>;
    }

    return (
      <Stack gap={2}>
        <Panel gap={2} title="Application Parameters">
          <Typography fontWeight="bold">
            Select the application(s) to edit parameters
          </Typography>
          <AppCheckboxGroup
            apps={applications.map((entry) => entry.app)}
            onChange={onClickAppIcon}
            selectedApps={selectedApps}
          />
          {appsChanged && (
            <Alert severity="info">
              You're about to delete certain application parameters. Please make
              sure all relevant applications are selected above, prior to
              submitting this form.
            </Alert>
          )}
        </Panel>
        {applications
          .filter(({ app }) => selectedApps.has(app.id))
          .map(({ app, schemas, version }) => {
            return (
              <AppParameterFormLayout
                app={app}
                deviceParams={deviceParams.filter((p) => p.app_id === app.id)}
                formErrors={fieldErrors}
                getFormApi={(form: () => AppFormApi) =>
                  registerAppForm(app.id, form)
                }
                key={app.id}
                network={network}
                networkParams={networkParams.filter((p) => p.app_id === app.id)}
                onRemove={removeAppParamForm}
                priority={priority}
                readonly={readonly}
                schemas={schemas}
                systemManagedParams={systemManagedParams}
                version={version}
              />
            );
          })}
      </Stack>
    );
  };

  const renderBottomActions = () => {
    return (
      <Stack alignItems="center" direction="row" gap={1}>
        <BackButton />
        {!readonly && (
          <Button disabled={submitted} onClick={handleSubmit}>
            Save
          </Button>
        )}
      </Stack>
    );
  };

  return (
    <Stack gap={2}>
      <Notification
        error={errList}
        success={successMessage}
        successChildren={successLink}
      />
      {hasAppRestrictions && (
        <Alert dataTestId="app-restriction-alert" dense severity="info">
          This network contains an application restriction. Devices will use the
          version indicated by the restriction.
        </Alert>
      )}
      {renderApps()}
      <Panel title="Custom Parameters">
        <FetchController.LegacyParams injectAs="legacyParams">
          <CustomParameterForm
            deviceParams={devCustomParams}
            network={network}
            networkParams={netCustomParams}
            priority={priority}
            readOnly={readonly}
            ref={(form: CustomParameterFormHandle | null) => {
              formRef.current = form;
            }}
          />
        </FetchController.LegacyParams>
      </Panel>
      <Panel>
        <StickyPageActions>{renderBottomActions()}</StickyPageActions>
      </Panel>
    </Stack>
  );
}
